import { cn } from "../lib/utils";
import { useRouter } from "@tanstack/react-router";
import { ChevronDown, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const programs = [
  { label: "Adult BJJ", href: "/programs/jiu-jitsu", isRoute: true },
  { label: "Kids BJJ", href: "/programs/kids-jiu-jitsu", isRoute: true },
  { label: "Teens BJJ", href: "/programs/teens-jiu-jitsu", isRoute: true },
  { label: "MMA", href: "/programs/mma", isRoute: true },
  { label: "Muay Thai", href: "/programs/muay-thai", isRoute: true },
  { label: "Wrestling", href: "/programs/wrestling", isRoute: true },
];

interface NavItem {
  label: string;
  href: string;
  isRoute?: boolean;
  submenu?: { label: string; href: string; isRoute?: boolean }[];
}

const navItems: NavItem[] = [
  { label: "Home", href: "/", isRoute: true },
  { label: "Programs", href: "/programs", isRoute: true, submenu: programs },
  { label: "Schedule", href: "/schedule", isRoute: true },
  { label: "Reviews", href: "/reviews", isRoute: true },
  { label: "About", href: "/about-us", isRoute: true },
  { label: "Contact", href: "/contact-us", isRoute: true },
];

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
}

interface NavDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function NavDrawer({ open, onClose }: NavDrawerProps) {
  const [programsOpen, setProgramsOpen] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const router = useRouter();

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Focus close button when drawer opens; reset submenu when closing
  useEffect(() => {
    if (open) {
      setTimeout(() => closeButtonRef.current?.focus(), 50);
    } else {
      setProgramsOpen(false);
    }
  }, [open]);

  // Auto-close drawer when viewport resizes back to desktop width
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 768) {
        onClose();
      }
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [onClose]);

  function handleNavClick(item: NavItem) {
    onClose();
    if (item.isRoute) {
      router.navigate({ to: item.href });
    } else {
      scrollTo(item.href.replace("#", ""));
    }
  }

  function handleAnchorClick(href: string, isRoute?: boolean) {
    onClose();
    if (isRoute) {
      router.navigate({ to: href });
    } else {
      scrollTo(href.replace("#", ""));
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-foreground/60 backdrop-blur-sm transition-opacity duration-300",
          open
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none",
        )}
        onClick={onClose}
        onKeyUp={(e) => e.key === "Enter" && onClose()}
        aria-hidden="true"
      />

      {/* Drawer panel — slides from right to left */}
      <aside
        aria-label="Navigation menu"
        aria-hidden={!open}
        className={cn(
          "fixed top-0 right-0 bottom-0 z-50 w-72 max-w-[85vw] flex flex-col",
          "bg-primary text-primary-foreground shadow-elevated",
          "transition-transform duration-300 ease-in-out",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        {/* Gold accent top bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />

        {/* Drawer header */}
        <div className="flex items-center justify-between px-6 pt-7 pb-5 border-b border-primary-foreground/15">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 p-1 rounded-lg bg-gradient-accent flex items-center justify-center flex-shrink-0 shadow-gold">
              <img src="/assets/images/superhero-jiu-jitsu-academy-logo-01.png" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-display font-black text-xs tracking-tight">
                SUPERHERO
              </span>
              <span className="font-display font-light text-[10px] tracking-widest opacity-70 uppercase">
                Jiu Jitsu Academy
              </span>
            </div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="p-2 rounded-md hover:bg-primary-foreground/10 transition-colors-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Close navigation"
            data-ocid="nav-drawer-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav items */}
        <nav
          className="flex-1 overflow-y-auto py-4 px-4"
          aria-label="Drawer navigation"
        >
          <ul className="flex flex-col gap-1">
            {navItems.map((item) => {
              if (item.submenu) {
                return (
                  <li key={item.label}>
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => handleNavClick(item)}
                        className="flex-1 text-left px-4 py-3 rounded-l-lg text-sm font-display font-semibold hover:bg-primary-foreground/10 transition-colors-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-ocid="nav-drawer-programs-link"
                      >
                        {item.label}
                      </button>
                      <button
                        type="button"
                        onClick={() => setProgramsOpen((p) => !p)}
                        className="px-3 py-3 rounded-r-lg hover:bg-primary-foreground/10 transition-colors-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-expanded={programsOpen}
                        aria-label="Toggle programs submenu"
                        data-ocid="nav-drawer-programs"
                      >
                        <ChevronDown
                          className={cn(
                            "w-4 h-4 text-gold transition-transform duration-200",
                            programsOpen && "rotate-180",
                          )}
                        />
                      </button>
                    </div>

                    {/* Submenu */}
                    <div
                      className={cn(
                        "overflow-hidden transition-all duration-300 ease-in-out",
                        programsOpen
                          ? "max-h-96 opacity-100"
                          : "max-h-0 opacity-0",
                      )}
                    >
                      <ul className="flex flex-col gap-0.5 pl-4 pt-1 pb-2">
                        {item.submenu.map((sub) => (
                          <li key={sub.label}>
                            <button
                              type="button"
                              onClick={() =>
                                handleAnchorClick(sub.href, sub.isRoute)
                              }
                              className="w-full text-left px-4 py-2.5 rounded-lg text-sm font-body text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10 transition-colors-fast flex items-center gap-2"
                              data-ocid={`nav-drawer-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                              {sub.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                );
              }

              return (
                <li key={item.label}>
                  <button
                    type="button"
                    onClick={() => handleNavClick(item)}
                    className="w-full text-left px-4 py-3 rounded-lg text-sm font-display font-semibold hover:bg-primary-foreground/10 transition-colors-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-ocid={`nav-drawer-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* CTA at bottom */}
        <div className="px-4 pb-8 pt-4 border-t border-primary-foreground/15">
          <button
            type="button"
            onClick={() => {
              onClose();
              router.navigate({ to: "/contact-us" });
            }}
            className="w-full py-3 px-6 rounded-xl font-display font-bold text-sm bg-gradient-accent text-foreground shadow-gold hover:opacity-90 active:scale-[0.98] transition-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-ocid="nav-drawer-cta"
          >
            Claim Your Free Class
          </button>
        </div>
      </aside>
    </>
  );
}
